import { AdminModules, CompanyModules, getPermissions, ProjectModules } from '@shared/constants';
import { PermissionType, Project, UserRole } from '@shared/models';

import { AuthService } from '../services/auth.service';

export function hasAdminPermission(
  module: AdminModules,
  permission: PermissionType,
  authService: AuthService
): boolean {
  if (authService.isUserInRole(UserRole.Admin)) {
    return true;
  }

  const permissionsList = getPermissions(authService.user.roles);
  if (!permissionsList) {
    return false;
  }

  return permissionsList[module]?.includes(permission) ?? false;
}

export function hasCompanyPermission(
  module: CompanyModules,
  permission: PermissionType,
  authService: AuthService
): boolean {
  if (authService.isUserInOneOfRoles([UserRole.Admin, UserRole.Ceo])) {
    return true;
  }

  const permissionsList = getPermissions(authService.user.roles);
  if (!permissionsList) {
    return false;
  }

  return permissionsList[module]?.includes(permission) ?? false;
}

export function hasProjectPermission(
  module: ProjectModules | undefined,
  permission: PermissionType,
  project: Project | undefined,
  authService: AuthService
): boolean {
  if (project?.isRemoved && permission !== PermissionType.view) {
    return false;
  }

  if (authService.isUserInOneOfRoles([UserRole.Admin])) {
    // INFO (Denis) - Admin can always do everything
    return true;
  }

  if (project && authService.user.companyId !== project.companyId) {
    // INFO (Denis) - Never allow cross-company operations
    return false;
  }

  if (authService.isUserInRole(UserRole.Ceo)) {
    return true; // INFO (Denis) - CEO can do everything in his company
  }

  if (authService.isUserInRole(UserRole.Stakeholder)) {
    if (permission === PermissionType.view) {
      return true; // INFO (Oktawian) - Stakeholder have the same permissions as CEO in his company, but only for viewing
    }
  }

  if (authService.isUserInRole(UserRole.ProjectManager)) {
    if (permission === PermissionType.create || permission === PermissionType.view) {
      return true; // INFO (Oktawian) - PM can view projects even if he is not assigned
    }

    const isAssignedToProject =
      project?.details?.responsibleProjectManagerUserId === authService.user.id ||
      project?.details?.assistantProjectManagersUserIds?.includes(authService.user.id);
    if (!isAssignedToProject) {
      return false;
    }
  }

  if (authService.isUserInRole(UserRole.Foreman)) {
    if (permission === PermissionType.create) {
      return true;
    }

    const isAssignedToProject =
      project?.details?.responsibleProjectManagerUserId === authService.user.id ||
      project?.details?.assistantProjectManagersUserIds?.includes(authService.user.id);
    if (!isAssignedToProject) {
      return false;
    }
  }

  const permissionsList = getPermissions(authService.user.roles);

  return permissionsList[module]?.includes(permission) ?? false;
}
